import { useState, useRef, useMemo } from "react";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button";
import { Input } from "@/project/karuna/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/project/karuna/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog";
import { Search, Trash2, Eye, Phone, Heart, IndianRupee } from "lucide-react";
import { useKarunaPageSize } from "@/project/karuna/hooks/useKarunaPageSize";
import { KARUNA_PAGE_SIZE_OPTIONS } from "@/project/karuna/constants/listPagination";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pagination, Popconfirm, Spin } from "antd";
import { createForm } from "@formily/core";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { notify } from "@/components/common/Notification";

const SCHEMA_NAME = "Karuna Donation";

function getInitials(name: string): string {
  return (name ?? "?")
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AdminDonations = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const { pageSize, setPageSize } = useKarunaPageSize();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const form = useMemo(() => createForm({}), []);
  const saveRef = useRef<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["donations-grid", page, pageSize],
    queryFn: () =>
      DynamicModuleService.getGridColItems(SCHEMA_NAME, page, pageSize),
  });

  const records: any[] = data?.results ?? (Array.isArray(data) ? data : []);
  const totalCount: number = data?.count ?? records.length;

  const filtered = records.filter(r =>
    [r.name, r.email, r.mobile, r.payment_reference]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handlePageChange = (newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize as (typeof KARUNA_PAGE_SIZE_OPTIONS)[number]);
      setPage(1);
      return;
    }
    setPage(newPage);
  };

  const handleView = (id: string) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const handleSave = async () => {
    await saveRef.current?.handleSave();
    setModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ["donations-grid"] });
  };

  const handleDelete = async (id: string) => {
    try {
      await DynamicModuleService.deleteItem(SCHEMA_NAME, id);
      notify.success("Deleted", "Donation entry removed successfully.");
      queryClient.invalidateQueries({ queryKey: ["donations-grid"] });
    } catch {
      notify.error("Delete Failed", "Could not delete the entry.");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-3xl font-bold">Donations</h1>
            <p className="mt-1 text-muted-foreground">
              All donation submissions from your website
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg border bg-white px-4 py-2 shadow-sm">
              <Heart className="h-5 w-5 text-red-500" />
              <span className="text-2xl font-bold text-primary">
                {totalCount}
              </span>
              <span className="text-sm text-muted-foreground">
                Total Donations
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-white px-4 py-2 shadow-sm">
              <IndianRupee className="h-5 w-5 text-green-600" />
              <span className="text-2xl font-bold text-green-600">
                {records
                  .reduce((sum, r) => sum + Number(r.custom_amount ?? 0), 0)
                  .toLocaleString("en-IN")}
              </span>
              <span className="text-sm text-muted-foreground">
                Total Amount
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="border-b bg-muted/20 px-6 py-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, mobile or ref..."
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Donor
                    </th>
                    <th className="p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Mobile
                    </th>
                    <th className="p-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <Spin size="large" />
                        <p className="mt-3 text-sm text-muted-foreground">
                          Loading donations...
                        </p>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <Heart className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
                        <p className="font-medium text-muted-foreground">
                          No donations found
                        </p>
                        <p className="text-sm text-muted-foreground/70">
                          {search
                            ? "Try a different search term"
                            : "Donations will appear here"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((record: any) => (
                      <tr
                        key={record.id}
                        className="group transition-colors hover:bg-muted/20"
                      >
                        {/* Donor name */}
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-400 to-red-600 text-xs font-bold text-white">
                              {getInitials(record.name)}
                            </div>
                            <span className="font-medium">
                              {record.name ?? "—"}
                            </span>
                          </div>
                        </td>

                        {/* Mobile */}
                        <td className="p-4">
                          <div className="flex items-center gap-1.5 text-sm">
                            <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span>{record.mobile ?? "—"}</span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1.5 px-3 text-xs"
                              onClick={() => handleView(record.id)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                            <Popconfirm
                              title="Delete this donation?"
                              description="This action cannot be undone."
                              onConfirm={() => handleDelete(record.id)}
                              okText="Delete"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5 px-3 text-xs text-red-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </Button>
                            </Popconfirm>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            {totalCount > 0 && (
              <div className="flex items-center justify-between border-t bg-muted/10 px-6 py-4">
                <span className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, totalCount)} of {totalCount}{" "}
                  entries
                </span>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  pageSizeOptions={KARUNA_PAGE_SIZE_OPTIONS.map(String)}
                  showSizeChanger
                  showQuickJumper
                  size="small"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Donation Entry</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto py-2">
            <SchemaFormAutoSubmit
              ref={saveRef}
              form={form}
              schemaName={SCHEMA_NAME}
              recordId={selectedId}
            />
          </div>
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminDonations;
